import BomArrow from "@assets/images/bom-arrow.svg";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from "@material-ui/core";
import MyTypography from "@saleor/components/MyTypography";
import StickyHorizontalScrollBar from "@saleor/components/StickyHorizontalScrollBar/StickyHorizontalScrollBar";
import TableCellHeader from "@saleor/components/TableCellHeader";
import { ListOfSaleOrderPendingSapQuery } from "@saleor/graphql";
import { renderCollection } from "@saleor/misc";
import { PendingOrderListSortField } from "@saleor/PendingOrder/urls";
import { sortAndMerge } from "@saleor/PendingOrder/utils";
import { SortPage } from "@saleor/types";
import { getArrowDirection } from "@saleor/utils/sort";
import classNames from "classnames";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import InlineSVG from "react-inlinesvg";
import { useIntl } from "react-intl";

import useStyles from "./styles";

interface OrderListTableAdminProps extends SortPage<PendingOrderListSortField> {
  productGroups: ListOfSaleOrderPendingSapQuery["listOfSaleOrderPendingSap"][0]["productGroups"];
}

const TableData: React.FC<OrderListTableAdminProps> = ({
  productGroups,
  sort,
  onSort
}) => {
  const [activeTab, setActiveTab] = useState<string>(
    productGroups[0]?.productGroup
  );

  const classes = useStyles();

  const onTabClick = tab => {
    setActiveTab(tab);
  };

  const tabList = productGroups?.map((item, index) => {
    if (index === 0) {
      return {
        id: item.productGroup,
        title: item.productGroup,
        code: index,
        first: true,
        index
      };
    }
    return {
      id: item.productGroup,
      title: item.productGroup,
      code: index,
      first: false,
      index
    };
  });

  return (
    <>
      <div className={classes.tabBox}>
        {renderCollection(tabList, tab => (
          <div
            key={tab.code}
            className={classNames(
              activeTab === tab.title ? classes.zMax : classes[tab.index]
            )}
          >
            <button
              id={tab.id}
              data-testid={tab.id}
              onClick={() => onTabClick(tab.title)}
              className={classNames(
                !tab.first && classes.tabB,
                activeTab === tab.title
                  ? classes.tableTabActive
                  : classes.tableTab,
                classes.buttonTab
              )}
            >
              <MyTypography
                variant="subtitle2"
                className={classNames(
                  activeTab === tab.title
                    ? classes.innerTabActive
                    : classes.innerTab
                )}
              >
                {tab.title}
              </MyTypography>
            </button>
          </div>
        ))}
      </div>
      {productGroups?.map((item, index) => (
        <OrderTable
          productGroupName={item.productGroup}
          activeTab={activeTab}
          productGroup={item}
          sort={sort}
          onSort={onSort}
          key={item.productGroup + " #" + index}
        />
      ))}
    </>
  );
};

export default TableData;
interface OrderTableProps {
  productGroupName: string;
  activeTab: string;
  productGroup: ListOfSaleOrderPendingSapQuery["listOfSaleOrderPendingSap"][0]["productGroups"][0];
  sort: Partial<{ asc: boolean; sort: PendingOrderListSortField }>;
  onSort: (field: PendingOrderListSortField, id?: string) => void;
}
const OrderTable = ({
  productGroupName,
  activeTab,
  productGroup,
  sort,
  onSort
}: OrderTableProps) => {
  const classes = useStyles();
  const intl = useIntl();

  const [item, setItems] = useState<
    ListOfSaleOrderPendingSapQuery["listOfSaleOrderPendingSap"][0]["productGroups"][0]
  >(productGroup);

  const [pendingOrderTempList] = useState<
    ListOfSaleOrderPendingSapQuery["listOfSaleOrderPendingSap"][0]["productGroups"][0]
  >(productGroup);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [, setReRender] = useState<boolean>(false);
  const formatDate = "DD/MM/YYYY";

  useEffect(() => {
    setReRender(prevState => !prevState);
    if (containerRef.current) {
      containerRef!.current!.scrollLeft = 0;
    }
  }, [activeTab]);
  function formatNumber(value) {
    if (value == null) {
      return "";
    }
    const parsedValue = parseFloat(value);
    const integerFormatter = new Intl.NumberFormat("en-US");
    const decimalFormatter = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3
    });

    if (Number.isInteger(parsedValue)) {
      return integerFormatter.format(parsedValue);
    } else {
      return decimalFormatter.format(parsedValue);
    }
  }
  useEffect(() => {
    const list = [...pendingOrderTempList.orderLines];

    const formattedList = list.map(item => ({
      ...item,
      confirmDate: item?.confirmDate
        ? moment(item?.confirmDate, formatDate).toDate()
        : item?.confirmDate,
      orderDate: item?.orderDate
        ? moment(item?.orderDate, formatDate).toDate()
        : item?.orderDate,
      itemNo: parseInt(item?.itemNo, 10),
      soNo: item?.soNo?.toString(),
      orderQty: formatNumber(item?.orderQty),
      pendingQty: formatNumber(item?.pendingQty),
      atpQty: formatNumber(item?.atpQty),
      ctpQty: formatNumber(item?.ctpQty),
      deliveryQty: formatNumber(item?.deliveryQty)
    }));

    const secondarySortKeys = ["soNo", "itemNo"];

    setItems({
      ...item,
      orderLines: sortAndMerge(formattedList, sort, secondarySortKeys).map(
        item => ({
          ...item,
          confirmDate: item?.confirmDate
            ? moment(item?.confirmDate).format(formatDate)
            : item?.confirmDate,
          orderDate: item?.orderDate
            ? moment(item?.orderDate).format(formatDate)
            : item?.orderDate,
          itemNo: item?.itemNo?.toString(),
          orderQty: item?.orderQty?.toString(),
          pendingQty: item?.pendingQty?.toString(),
          atpQty: item?.atpQty?.toString(),
          ctpQty: item?.ctpQty?.toString(),
          deliveryQty: item?.deliveryQty?.toString()
        })
      )
    });
  }, [sort]);

  const isBomChild = item => {
    if (item?.parentItemNo !== "" && item?.bomFlag === true) {
      return true;
    } else {
      return false;
    }
  };

  if (productGroupName === activeTab) {
    return (
      <div className={classes.boxTable} ref={containerRef}>
        <TableContainer className={classes.tableContainer}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCellHeader
                  style={{ minWidth: 158 }}
                  className={classNames(classes.freeze_1)}
                  direction={
                    sort.sort === PendingOrderListSortField.soldTo
                      ? getArrowDirection(sort.asc)
                      : undefined
                  }
                  arrowPosition="right"
                  onClick={() => onSort(PendingOrderListSortField.soldTo)}
                >
                  <MyTypography variant="subtitle3">
                    {intl.formatMessage({
                      defaultMessage: "ชื่อบริษัท"
                    })}
                  </MyTypography>
                  <span className={classes.subHeading}> (Sales Org Name) </span>
                </TableCellHeader>
                <TableCellHeader
                  style={{ minWidth: 150 }}
                  direction={
                    sort.sort === PendingOrderListSortField.poNo
                      ? getArrowDirection(sort.asc)
                      : undefined
                  }
                  arrowPosition="right"
                  onClick={() => onSort(PendingOrderListSortField.poNo)}
                >
                  <MyTypography variant="subtitle3">
                    {intl.formatMessage({
                      defaultMessage: "เลขที่ใบสั่งซื้อ P/O"
                    })}
                  </MyTypography>
                  <span className={classes.subHeading}> (PO No.) </span>
                </TableCellHeader>
                <TableCellHeader
                  style={{ minWidth: 150 }}
                  direction={
                    sort.sort === PendingOrderListSortField.soNo
                      ? getArrowDirection(sort.asc)
                      : undefined
                  }
                  arrowPosition="right"
                  onClick={() => onSort(PendingOrderListSortField.soNo)}
                >
                  <MyTypography variant="subtitle3">
                    {intl.formatMessage({
                      defaultMessage: "เลขที่ใบสั่งซื้อ S/O"
                    })}
                  </MyTypography>
                  <span className={classes.subHeading}> (SO No.) </span>
                </TableCellHeader>
                <TableCellHeader
                  width="320"
                  style={{ minWidth: 320 }}
                  direction={
                    sort.sort === PendingOrderListSortField.shipTo
                      ? getArrowDirection(sort.asc)
                      : undefined
                  }
                  arrowPosition="right"
                  onClick={() => onSort(PendingOrderListSortField.shipTo)}
                >
                  <MyTypography variant="subtitle3">
                    {intl.formatMessage({
                      defaultMessage: "สถานที่จัดส่ง"
                    })}
                  </MyTypography>
                  <span className={classes.subHeading}> (Ship-to) </span>
                </TableCellHeader>
                <TableCellHeader
                  style={{ minWidth: 125 }}
                  direction={
                    sort.sort === PendingOrderListSortField.orderDate
                      ? getArrowDirection(sort.asc)
                      : undefined
                  }
                  arrowPosition="right"
                  onClick={() => onSort(PendingOrderListSortField.orderDate)}
                >
                  <MyTypography variant="subtitle3">
                    {intl.formatMessage({
                      defaultMessage: "วันที่สั่งซื้อ"
                    })}
                  </MyTypography>
                  <span className={classes.subHeading}> (Create Date) </span>
                </TableCellHeader>
                <TableCellHeader
                  style={{ minWidth: 120 }}
                  direction={
                    sort.sort === PendingOrderListSortField.confirmDate
                      ? getArrowDirection(sort.asc)
                      : undefined
                  }
                  arrowPosition="right"
                  onClick={() => onSort(PendingOrderListSortField.confirmDate)}
                >
                  <MyTypography variant="subtitle3">
                    {intl.formatMessage({
                      defaultMessage: "วันที่ประมาณการส่งมอบ"
                    })}
                  </MyTypography>
                </TableCellHeader>
                <TableCellHeader
                  style={{ minWidth: 110 }}
                  direction={
                    sort.sort === PendingOrderListSortField.itemNo
                      ? getArrowDirection(sort.asc)
                      : undefined
                  }
                  arrowPosition="right"
                  onClick={() => onSort(PendingOrderListSortField.itemNo)}
                >
                  <MyTypography variant="subtitle3">
                    {intl.formatMessage({
                      defaultMessage: "ลำดับที่"
                    })}
                  </MyTypography>
                  <span className={classes.subHeading}>(Item No.)</span>
                </TableCellHeader>
                <TableCellHeader
                  width="232"
                  style={{ minWidth: 232 }}
                  direction={
                    sort.sort === PendingOrderListSortField.materialCode
                      ? getArrowDirection(sort.asc)
                      : undefined
                  }
                  arrowPosition="right"
                  onClick={() => onSort(PendingOrderListSortField.materialCode)}
                >
                  <MyTypography variant="subtitle3">
                    {intl.formatMessage({
                      defaultMessage: "รายการสินค้า"
                    })}
                  </MyTypography>
                  <span className={classes.subHeading}> (Material Code) </span>
                </TableCellHeader>
                <TableCellHeader
                  width="320"
                  style={{ minWidth: 320 }}
                  direction={
                    sort.sort === PendingOrderListSortField.materialDescription
                      ? getArrowDirection(sort.asc)
                      : undefined
                  }
                  arrowPosition="right"
                  onClick={() =>
                    onSort(PendingOrderListSortField.materialDescription)
                  }
                >
                  <MyTypography variant="subtitle3">
                    {intl.formatMessage({
                      defaultMessage: "รายละเอียดสินค้า"
                    })}
                  </MyTypography>
                  <span className={classes.subHeading}>
                    {" "}
                    (Material Description)
                  </span>
                </TableCellHeader>
                <TableCellHeader
                  style={{ minWidth: 120 }}
                  direction={
                    sort.sort === PendingOrderListSortField.orderQty
                      ? getArrowDirection(sort.asc)
                      : undefined
                  }
                  arrowPosition="right"
                  onClick={() => onSort(PendingOrderListSortField.orderQty)}
                >
                  <MyTypography variant="subtitle3">
                    {intl.formatMessage({
                      defaultMessage: "จำนวนสั่งซื้อ"
                    })}
                  </MyTypography>
                  <span className={classes.subHeading}> (Order Qty.) </span>
                </TableCellHeader>
                <TableCellHeader
                  style={{ minWidth: 137 }}
                  direction={
                    sort.sort === PendingOrderListSortField.pendingQty
                      ? getArrowDirection(sort.asc)
                      : undefined
                  }
                  arrowPosition="right"
                  onClick={() => onSort(PendingOrderListSortField.pendingQty)}
                >
                  <MyTypography variant="subtitle3">
                    {intl.formatMessage({
                      defaultMessage: "จำนวนค้างส่ง"
                    })}
                  </MyTypography>
                  <span className={classes.subHeading}> (Pending Qty.) </span>
                </TableCellHeader>
                <TableCellHeader
                  style={{ minWidth: 122 }}
                  direction={
                    sort.sort === PendingOrderListSortField.atpQty
                      ? getArrowDirection(sort.asc)
                      : undefined
                  }
                  arrowPosition="right"
                  onClick={() => onSort(PendingOrderListSortField.atpQty)}
                >
                  <MyTypography variant="subtitle3">
                    {intl.formatMessage({
                      defaultMessage: "พร้อมส่ง"
                    })}
                  </MyTypography>
                  <span className={classes.subHeading}> (ATP Qty.) </span>
                </TableCellHeader>
                <TableCellHeader
                  style={{ minWidth: 112 }}
                  direction={
                    sort.sort === PendingOrderListSortField.ctpQty
                      ? getArrowDirection(sort.asc)
                      : undefined
                  }
                  arrowPosition="right"
                  onClick={() => onSort(PendingOrderListSortField.ctpQty)}
                >
                  <MyTypography variant="subtitle3">
                    {intl.formatMessage({
                      defaultMessage: "รอผลิต"
                    })}
                  </MyTypography>
                  <span className={classes.subHeading}> (CTP Qty.) </span>
                </TableCellHeader>
                <TableCellHeader
                  style={{ minWidth: 132 }}
                  direction={
                    sort.sort === PendingOrderListSortField.deliveryQty
                      ? getArrowDirection(sort.asc)
                      : undefined
                  }
                  arrowPosition="right"
                  onClick={() => onSort(PendingOrderListSortField.deliveryQty)}
                >
                  <MyTypography variant="subtitle3">
                    {intl.formatMessage({
                      defaultMessage: "ส่งแล้ว"
                    })}
                  </MyTypography>
                  <span className={classes.subHeading}> (Delivery Qty.) </span>
                </TableCellHeader>
                <TableCellHeader
                  style={{ minWidth: 80 }}
                  direction={
                    sort.sort === PendingOrderListSortField.saleUnit
                      ? getArrowDirection(sort.asc)
                      : undefined
                  }
                  arrowPosition="right"
                  onClick={() => onSort(PendingOrderListSortField.saleUnit)}
                >
                  <MyTypography variant="subtitle3">
                    {intl.formatMessage({
                      defaultMessage: "หน่วย"
                    })}
                  </MyTypography>
                  <span className={classes.subHeading}> (Unit) </span>
                </TableCellHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {item.orderLines.map((orderLine, index) => (
                <>
                  <TableRow key={index}>
                    <TableCell
                      className={classNames(classes.freeze_1, classes.zIndex1)}
                    >
                      <MyTypography variant="body3">
                        {orderLine?.saleOrg ?? ""}
                      </MyTypography>
                    </TableCell>
                    <TableCell>
                      <MyTypography variant="body3">
                        {orderLine?.poNo ?? ""}
                      </MyTypography>
                    </TableCell>
                    <TableCell>
                      <MyTypography variant="body3">
                        {orderLine?.soNo ?? ""}
                      </MyTypography>
                    </TableCell>
                    <TableCell>
                      <MyTypography variant="body3">
                        {orderLine?.shipTo ?? ""}
                      </MyTypography>
                    </TableCell>
                    <TableCell>
                      <MyTypography variant="body3">
                        {orderLine?.orderDate ?? ""}
                      </MyTypography>
                    </TableCell>
                    <TableCell>
                      <MyTypography variant="body3">
                        {orderLine?.confirmDate ?? ""}
                      </MyTypography>
                    </TableCell>
                    <TableCell>
                      <MyTypography
                        variant={isBomChild(orderLine) ? "body3" : "subtitle3"}
                      >
                        {orderLine?.itemNo ?? ""}
                      </MyTypography>
                    </TableCell>
                    <TableCell
                      className={classes.rowFlex}
                      style={{ whiteSpace: "nowrap" }}
                    >
                      <MyTypography
                        style={{ display: "flex" }}
                        variant={isBomChild(orderLine) ? "body3" : "subtitle3"}
                      >
                        {isBomChild(orderLine) ? (
                          <InlineSVG
                            className={
                              getArrowDirection(sort.asc) === "asc"
                                ? ""
                                : classes?.flip
                            }
                            src={BomArrow}
                            width={24}
                            height={24}
                            style={{ marginRight: "10px" }}
                          />
                        ) : null}
                        {orderLine?.materialCode ?? ""}
                      </MyTypography>
                    </TableCell>
                    <TableCell>
                      <MyTypography
                        variant={isBomChild(orderLine) ? "body3" : "subtitle3"}
                      >
                        {orderLine?.materialDescription ?? ""}
                      </MyTypography>
                    </TableCell>
                    <TableCell>
                      <MyTypography variant="body3">
                        {orderLine?.orderQty ?? ""}
                      </MyTypography>
                    </TableCell>
                    <TableCell>
                      <MyTypography variant="body3">
                        {orderLine?.pendingQty === "NaN"
                          ? ""
                          : orderLine?.pendingQty}
                      </MyTypography>
                    </TableCell>
                    <TableCell>
                      <MyTypography variant="body3">
                        {orderLine?.atpQty === "NaN" ? "" : orderLine?.atpQty}
                      </MyTypography>
                    </TableCell>
                    <TableCell>
                      <MyTypography variant="body3">
                        {orderLine?.ctpQty === "NaN" ? "" : orderLine?.ctpQty}
                      </MyTypography>
                    </TableCell>
                    <TableCell>
                      <MyTypography variant="body3">
                        {orderLine?.deliveryQty === "NaN"
                          ? ""
                          : orderLine?.deliveryQty}
                      </MyTypography>
                    </TableCell>
                    <TableCell>
                      <MyTypography variant="body3">
                        {orderLine?.saleUnit ?? ""}
                      </MyTypography>
                    </TableCell>
                  </TableRow>
                  {index === item.orderLines.length - 1
                    ? item?.summary?.map(summaryItem => (
                        <TableRow className="tableRowFooter">
                          <TableCell colSpan={9} align="right">
                            <MyTypography
                              variant="subtitle3"
                              style={{ textAlign: "right" }}
                            >
                              {intl.formatMessage({
                                defaultMessage: "Total"
                              })}
                              <span className={classes.subTxt}>
                                ({summaryItem?.saleUnit})
                              </span>
                            </MyTypography>
                          </TableCell>
                          <TableCell>
                            <MyTypography variant="body3">
                              {formatNumber(summaryItem?.orderQty)}
                            </MyTypography>
                          </TableCell>
                          <TableCell>
                            <MyTypography variant="body3">
                              {formatNumber(summaryItem?.pendingQty)}
                            </MyTypography>
                          </TableCell>
                          <TableCell>
                            <MyTypography variant="body3">
                              {formatNumber(summaryItem?.atpQty)}
                            </MyTypography>
                          </TableCell>
                          <TableCell>
                            <MyTypography variant="body3">
                              {formatNumber(summaryItem?.ctpQty)}
                            </MyTypography>
                          </TableCell>
                          <TableCell>
                            <MyTypography variant="body3">
                              {formatNumber(summaryItem?.deliveryQty)}
                            </MyTypography>
                          </TableCell>
                          <TableCell>
                            <MyTypography variant="body3">
                              {summaryItem?.saleUnit}
                            </MyTypography>
                          </TableCell>
                        </TableRow>
                      ))
                    : null}
                </>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <StickyHorizontalScrollBar container={containerRef} />
      </div>
    );
  }
  return <></>;
};
